import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { getYears, initPrjGoalData } from '../../utils/constCodeUtils';
import { useSelector, useDispatch } from 'react-redux';
import { setUserConfig, unloadUserConfig, viewUserConfig } from '../../modules/staticsModule/userConfig';
import * as yup from 'yup';
import { useFormik } from 'formik';
import _ from 'lodash';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, CircularProgress, Divider } from '@mui/material';

import * as staticsApi from '../../lib/api/statics';
import YearGoalForm from '../../components/goalsetting/YearGoalForm';
import UserGoalTable from '../../components/goalsetting/UserGoalTable';

const UserGoalListContainer = ({ user }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [years, setYears] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Redux 데이터 로드
    const { userConfig, loading } = useSelector(({ staticsModule, loading }) => ({
        userConfig: staticsModule.userConfig.userConfig,
        loading: loading['statics/VIEW_USER_CONFIG']
    }));

    useEffect(() => {
        setYears(getYears(3, 1));
    }, []);

    useEffect(() => {
        if (user && selectedYear) {
            dispatch(viewUserConfig({ "statYear": selectedYear, "usrUuid": user?.usrUuid }));
        }
        return () => dispatch(unloadUserConfig());
    }, [user, selectedYear, dispatch]);

    const initialValues = {
        ptargetSales: Array.isArray(userConfig) ? _.sumBy(userConfig, 'ptargetSales') : 0,
        ptargetSalesProfit: Array.isArray(userConfig) ? _.sumBy(userConfig, 'ptargetSalesProfit') : 0,
        ptargetOperatingProfit: Array.isArray(userConfig) ? _.sumBy(userConfig, 'ptargetOperatingProfit') : 0, // 추가
        monthData: Array.isArray(userConfig) && userConfig.length > 0 
            ? userConfig.map(d => ({ 
                sales: d.ptargetSales || 0, 
                profit: d.ptargetSalesProfit || 0,
                operatingProfit: d.ptargetOperatingProfit || 0 // 추가
            }))
            : Array.from({ length: 12 }, () => ({ sales: 0, profit: 0, operatingProfit: 0 })) // 초기 구조 변경
    };

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: yup.object({
            ptargetSales: yup.number().required('필수입력').min(0),
            ptargetSalesProfit: yup.number().required('필수입력').min(0),
            ptargetOperatingProfit: yup.number().required('필수입력').min(0), // 유효성 검사 추가
        }),
        onSubmit: async (values) => {
            // API 전송 데이터 구조에 영업이익 포함
            const monthGoals = values.monthData.map((item, index) => ({
                month: index + 1,
                ptargetSales: Number(item.sales || 0),
                ptargetSalesProfit: Number(item.profit || 0),
                ptargetOperatingProfit: Number(item.operatingProfit || 0) // 추가
            }));

            try {
                const res = await staticsApi.addStaticsConf({
                    usrUuid: user.usrUuid,
                    year: selectedYear,
                    // 총합계 필드명은 백엔드 API 사양에 따라 포함 여부 결정 (필요시 추가)
                    ptargetSales: values.ptargetSales,
                    ptargetSalesProfit: values.ptargetSalesProfit,
                    ptargetOperatingProfit: values.ptargetOperatingProfit,
                    monthGoals
                });
                
                if (res.data) {
                    dispatch(setUserConfig(res.data));
                    enqueueSnackbar("목표 설정이 저장되었습니다", { variant: 'success' });
                }
            } catch (err) {
                enqueueSnackbar(err.response?.data?.message || err.message, { variant: 'error' });
            } finally {
                formik.setSubmitting(false);
            }
        }
    });


    const handleChangeYear = (event) => setSelectedYear(event.target.value);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, alignItems: 'center' }}>
                <Box sx={{ minWidth: 120 }}>
                    <FormControl variant="standard" fullWidth>
                        <InputLabel>기준년도</InputLabel>
                        <Select value={selectedYear} onChange={handleChangeYear}>
                            {years?.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
                {(!Array.isArray(userConfig) || userConfig.length === 0) && (
                    <Typography variant="body2" color="error">설정된 목표값이 없습니다.</Typography>
                )}
            </Box>
            <Divider sx={{ my: 1 }} />
            <YearGoalForm formik={formik} />
        </Box>
    );
};

export default UserGoalListContainer;
